import React, { useState } from 'react';
import MiniLeaderboard from './MiniLeaderboard';
import { HostPlayingScoreStrip } from './HostPlayingScoreStrip';
import { FeelingAloneAddAi } from './FeelingAloneAddAi';

interface ConditionalLeaderboardProps {
  /** Whether the host is playing (if true, show leaderboard; if false, hide it) */
  hostIsPlaying: boolean;
  /** Array of players */
  players: Array<{
    id: string;
    name: string;
    score: number;
    avatar?: string;
  }>;
  /** Current player's ID */
  myPlayerId: string;
  /** Optional set of player IDs who have answered (for highlighting) */
  answeredPlayerIds?: Set<string>;
  pointsThisQuestion?: number | null;
  showQuestionPointsLine?: boolean;
}

/**
 * Survives remounts when player screens swap question ↔ waiting branches
 * (each branch historically mounted its own ConditionalLeaderboard).
 * Starts collapsed; once the user expands, stays open until they collapse.
 */
let persistedStandingsExpanded = false;

/**
 * Conditionally displays standings when host is playing.
 * Collapsed by default: score strip (rank, name, points) with expand arrow.
 * Expanded: full mini leaderboard list below the strip.
 * Once expanded, stays open across question transitions until the user collapses it.
 */
export const ConditionalLeaderboard: React.FC<ConditionalLeaderboardProps> = ({
  hostIsPlaying,
  players,
  myPlayerId,
  answeredPlayerIds,
  pointsThisQuestion = null,
  showQuestionPointsLine = false,
}) => {
  const [expanded, setExpanded] = useState(persistedStandingsExpanded);

  if (!hostIsPlaying) {
    return null;
  }

  const toggleExpanded = () => {
    setExpanded((prev) => {
      const next = !prev;
      persistedStandingsExpanded = next;
      return next;
    });
  };

  return (
    <div className="host-playing-standings" data-testid="host-playing-standings">
      <HostPlayingScoreStrip
        hostIsPlaying={hostIsPlaying}
        players={players}
        myPlayerId={myPlayerId}
        pointsThisQuestion={pointsThisQuestion}
        showQuestionPointsLine={showQuestionPointsLine}
        expanded={expanded}
        onToggleExpand={toggleExpanded}
      />
      {expanded && (
        <MiniLeaderboard
          players={players}
          myPlayerId={myPlayerId}
          answeredPlayerIds={answeredPlayerIds}
        />
      )}
      <FeelingAloneAddAi variant="compact" />
    </div>
  );
};
